import { type DateFnsDateFormat, getUserDateFormat, toDateFnsFormat } from '@sit/client-shared';
import { type AppLocale, toAppLocale } from '@sit/core';
import { type UserType } from '@sit/openapi';
import { createAtom, useSelector } from '@tanstack/react-store';
import { useGetUser } from '@web/api/users/use-get-user';
import { useIsAuthenticated } from '@web/auth/use-is-authenticated';

const enableLocale = import.meta.env.VITE_APP_ENABLE_LOCALE === 'true';

/** Developer settings override; `undefined` means "use the user's locale". */
const localeOverrideAtom = createAtom<AppLocale | undefined>(undefined);

export function setLocaleOverride(locale: AppLocale | undefined) {
  localeOverrideAtom.set(locale);
}

export function useLocaleOverride() {
  return useSelector(localeOverrideAtom);
}

/** Developer settings override; `undefined` means "use the user's date format". */
const dateFormatOverrideAtom = createAtom<DateFnsDateFormat | undefined>(undefined);

export function setDateFormatOverride(dateFormat: DateFnsDateFormat | undefined) {
  dateFormatOverrideAtom.set(dateFormat);
}

export function useDateFormatOverride() {
  return useSelector(dateFormatOverrideAtom);
}

function selectLocale(data: UserType) {
  return {
    locale: data.locale,
    dateFormat: data.dateFormat ? toDateFnsFormat(data.dateFormat) : undefined,
  };
}

export function useUserLocale() {
  const isAuthenticated = useIsAuthenticated();
  const overriddenLocale = useLocaleOverride();
  const overriddenDateFormat = useDateFormatOverride();
  const userLocale = useGetUser({
    enabled: isAuthenticated && enableLocale,
    select: selectLocale,
  }).data;
  const locale = overriddenLocale ?? toAppLocale(userLocale?.locale);
  const dateFormat = overriddenDateFormat ?? userLocale?.dateFormat;
  return { locale, dateFormat };
}

export function useDateFormat(): DateFnsDateFormat {
  const override = useDateFormatOverride();
  const userData = useGetUser().data;
  return override ?? getUserDateFormat(userData);
}
